import UAParser from "ua-parser-js";
import { formatIltDateTime } from "../utils/datetime";
import { isIlt, isOnline } from "../utils/course_utils";
import il8n from "../i18n";

const userAssignmentsService = {};

const calendarTypes = [
  { title: "Google Calendar", type: "google" },
  { title: "Outlook Calendar", type: "outlook" },
  { title: "iCal", type: "ical" }
];

userAssignmentsService.getAssignmentCalendars = (
  assignment,
  courseSection = null,
  shortCourse = false
) => {
  const enrolledVersion = assignment.versions.find(
    version => version.id == assignment.enrolledTrainingObjectId
  );
  let courseLink = getCourseLink(assignment, courseSection);
  let duration = enrolledVersion
    ? enrolledVersion.duration
    : assignment.duration;

  if (courseSection) {
    duration =
      duration == 0
        ? `${courseSection.timeEstimate[0]}${courseSection.timeEstimate[1]}`
        : duration;
  }

  duration = parseInt(duration);

  let startDate = new Date();
  let endDate = new Date();
  let allDay = false;
  if (shortCourse) {
    endDate = new Date(startDate);
    endDate.setMinutes(startDate.getMinutes() + duration);
  } else if (
    isOnline(assignment) &&
    assignment.assignmentType === "self assigned"
  ) {
    endDate = new Date(startDate);
    endDate.setMinutes(startDate.getMinutes() + parseInt(duration));
  } else if (isIlt(assignment) && enrolledVersion) {
    startDate = formatIltDateTime(
      assignment.scheduledDate,
      assignment.scheduledTime,
      assignment.timeZone
    );
    endDate = formatIltDateTime(
      enrolledVersion.endDate,
      enrolledVersion.endTime,
      assignment.timeZone
    );
  } else {
    allDay = true;
    startDate = new Date(assignment.dueDate);
    endDate = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate() + 1,
      startDate.getHours(),
      startDate.getMinutes(),
      startDate.getSeconds()
    );
  }

  let courseTitle = shortCourse
    ? il8n.t("calendarShortCourse") + assignment.title
    : assignment.title;

  const calendars = calendarTypes.map(calendar => ({
    title: calendar.title,
    value: generateCalendarAppLinkForCourse(
      calendar.type,
      courseTitle,
      assignment.content,
      courseLink,
      assignment.id,
      duration,
      startDate,
      endDate,
      allDay
    ),
    courseTitle: courseTitle,
    courseDescription: assignment.content,
    linkToTheFirstActivity: courseLink,
    courseId: assignment.id,
    duration: getCourseDuration(duration),
    dtStart: getCalendarDate(startDate),
    dtEnd: getCalendarDate(endDate),
    allDay: allDay
  }));
  return calendars;
};

function getCalendarDate(date, allDay = false) {
  let formattedDate = date.toISOString().replace(/-|:|\.\d\d\d/g, "");
  if (allDay) {
    formattedDate = formattedDate.slice(0, -8);
  }
  return formattedDate;
}

function getCourseLink(assignment, courseSection) {
  if (!courseSection || !courseSection.courseContentables) {
    return getLPAorMyItemsPageLink(assignment.assignmentType, assignment.id);
  }

  if (assignment.enrolledTrainingObjectId && courseSection.isOverview) {
    return getGettingStartedLink(
      assignment.assignedTrainingObjectId,
      assignment.enrolledTrainingObjectId,
      courseSection.folder
    );
  } else if (courseSection.isOverview) {
    return getGettingStartedLink(
      assignment.assignedTrainingObjectId,
      assignment.versions[0].id,
      courseSection.folder
    );
  } else {
    return getFirstActivityLink(
      assignment.id,
      courseSection.courseContentables[0].id,
      courseSection.folder
    );
  }
}

function getCourseDuration(duration) {
  const hours = Math.floor(duration / 60);
  const minutes = !!hours ? duration % 60 : duration;
  let courseDuration = duration === 0 ? "0 Mins" : "";
  if (hours > 1) {
    courseDuration = `${hours} Hrs`;
  } else if (hours === 1) {
    courseDuration = `${hours} Hr`;
  }
  if (minutes) {
    courseDuration = courseDuration + `${minutes} Mins`;
  }
  return courseDuration;
}

function getFirstActivityLink(assignmentId, activityId, folder) {
  return `${window.location.origin}/learning-center/attempts/${assignmentId}/activities/${activityId}?folder=${folder}`;
}

function getGettingStartedLink(assignedTrainingObjectId, versionId, folder) {
  return `${window.location.origin}/learning-center/course-overview/${assignedTrainingObjectId}/${versionId}?folder=${folder}`;
}

function getLPAorMyItemsPageLink(type, id) {
  if (type === "self assigned") {
    return `${window.location.origin}/learning-center/learn/library/my-items/my-courses?assignment=${id}`;
  } else {
    return `${window.location.origin}/learning-center/learn/my-learning-path/list-view?assignment=${id}`;
  }
}

function generateCalendarAppLinkForCourse(
  type,
  courseTitle,
  fullCourseDescription,
  linkToTheFirstActivity,
  courseId,
  duration,
  startDate,
  endDate,
  allDay
) {
  let userAgentParsed = new UAParser(navigator.userAgent);
  let osName = userAgentParsed.getResult().os.name;
  if (type === "google") {
    if (osName === "iOS") {
      return (
        "googlecalendar://render?action=TEMPLATE&text=" +
        courseTitle +
        "&dates=" +
        getCalendarDate(startDate, allDay) +
        "/" +
        getCalendarDate(endDate, allDay) +
        '&details=<a href="' +
        linkToTheFirstActivity +
        '">' +
        courseTitle +
        "</a> - " +
        getCourseDuration(duration) +
        "%0A" +
        fullCourseDescription +
        "&sf=true&output=xml"
      );
    } else if (osName === "Android") {
      return (
        "intent://www.google.com/calendar/render?action=TEMPLATE&text=" +
        courseTitle +
        "&dates=" +
        getCalendarDate(startDate, allDay) +
        "/" +
        getCalendarDate(endDate, allDay) +
        '&details=<a href="' +
        linkToTheFirstActivity +
        '">' +
        courseTitle +
        "</a> - " +
        getCourseDuration(duration) +
        "%0A" +
        fullCourseDescription +
        "&sf=true&output=xml&event#Intent;scheme=https;package=com.google.android.calendar;end"
      );
    } else {
      return (
        "https://www.google.com/calendar/render?action=TEMPLATE&text=" +
        courseTitle +
        "&dates=" +
        getCalendarDate(startDate, allDay) +
        "/" +
        getCalendarDate(endDate, allDay) +
        '&details=<a href="' +
        linkToTheFirstActivity +
        '">' +
        courseTitle +
        "</a> - " +
        getCourseDuration(duration) +
        "%0A" +
        fullCourseDescription +
        "&sf=true&output=xml"
      );
    }
  }
  if (type === "outlook") {
    if (osName === "Android") {
      return (
        "ms-outlook://events/new?title=" +
        courseTitle +
        "&start=" +
        getCalendarDate(startDate, allDay) +
        "&end=" +
        getCalendarDate(endDate, allDay) +
        '&description=<a href="' +
        linkToTheFirstActivity +
        '">' +
        courseTitle +
        "</a> - " +
        getCourseDuration(duration) +
        "<br>" +
        fullCourseDescription
      );
    } else if (osName === "iOS") {
      return (
        "ms-outlook://events/new?title=" +
        courseTitle +
        "&start=" +
        getCalendarDate(startDate, allDay) +
        "&end=" +
        getCalendarDate(endDate, allDay) +
        "&description=" +
        courseTitle +
        " - " +
        getCourseDuration(duration) +
        "%0A" +
        linkToTheFirstActivity +
        "%0A" +
        fullCourseDescription
      );
    }
  }
  if (type === "ical") {
    if (osName === "iOS") {
      return (
        "my_courses/" +
        courseId +
        "/calendar_events?title=ical&courseTitle=" +
        courseTitle +
        "&courseDescription=" +
        fullCourseDescription +
        "&linkToTheFirstActivity=" +
        linkToTheFirstActivity +
        "&duration=" +
        getCourseDuration(duration) +
        "&dtStart=" +
        getCalendarDate(startDate, allDay) +
        "&dtEnd=" +
        getCalendarDate(endDate, allDay) +
        "&device=iOS"
      );
    }
  }
}

export default userAssignmentsService;
